import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaArrowLeft, FaPlus, FaTimes, FaEdit, FaTrash, FaCopy, FaBan, FaEyeSlash, FaEye, FaPaperPlane, FaUpload, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './AdminScreenerStocks.css';
import { useAuth } from '../../../context/AuthContext.tsx';
import screenerApi from '../../../services/screenerApi.ts';
import emailApi from '../../../services/emailApi.ts';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

interface ScreenerMaster {
  id: number;
  code: string;
  name: string;
  category: string;
  chartView?: string; // 'CLASSIC' | 'DRAWING'
}

interface ScreenerStock {
  id: number;
  symbol: string;
  stockName: string;
  exchange: string;
  capturedPrice: number;
  currentPrice: number | null;
  capturedOn: string;
  maxUpside: number | null;
  resistanceLevel: number | null;
  supportLevel: number | null;
  sector: string | null;
  industry: string | null;
  notes: string | null;
  eliminated: boolean;
  hiddenForFutureUsers: boolean;
  isActive: boolean;
}

interface StockFormData {
  screenerCode: string;
  symbol: string;
  stockName: string;
  exchange: string;
  capturedPrice: string;
  capturedOn: string;
  resistanceLevel: string;
  supportLevel: string;
  sector: string;
  industry: string;
  notes: string;
}

const AdminScreenerStocks: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Check if user is admin
  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return user.roles?.some(role =>
      role === 'ADMIN' || role === 'ROLE_ADMIN' || role.toUpperCase().includes('ADMIN')
    );
  }, [isAuthenticated, user]);

  const [stocks, setStocks] = useState<ScreenerStock[]>([]);
  const [screeners, setScreeners] = useState<ScreenerMaster[]>([]);
  const [selectedScreener, setSelectedScreener] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingStock, setDeletingStock] = useState<ScreenerStock | null>(null);
  const [isEliminateDialogOpen, setIsEliminateDialogOpen] = useState(false);
  const [eliminatingStock, setEliminatingStock] = useState<ScreenerStock | null>(null);
  const [cloneDrawings, setCloneDrawings] = useState(false);
  const [cloningSourceStockId, setCloningSourceStockId] = useState<number | null>(null);

  // Upload stocks dialog state
  const [isUploadDialogOpen, setIsUploadDialogOpen]     = useState(false);
  const [uploadFile, setUploadFile]                     = useState<File | null>(null);
  const [uploadExchange, setUploadExchange]             = useState('NSE');
  const [uploading, setUploading]                       = useState(false);
  const [uploadResult, setUploadResult]                 = useState<{
    total: number; inserted: number; skipped: number; failed: number;
    newStocks: { symbol: string; name: string }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Movement email dialog state
  const [isMovementDialogOpen, setIsMovementDialogOpen] = useState(false);
  const [movementStock, setMovementStock] = useState<ScreenerStock | null>(null);
  const [movementPercent, setMovementPercent] = useState('');
  const [movementRecipientType, setMovementRecipientType] = useState<'ALL' | 'PREMIUM' | 'FREE'>('ALL');
  const [sendingMovement, setSendingMovement] = useState(false);

  const [formData, setFormData] = useState<StockFormData>({
    screenerCode: '',
    symbol: '',
    stockName: '',
    exchange: 'NSE',
    capturedPrice: '',
    capturedOn: new Date().toISOString().split('T')[0],
    resistanceLevel: '',
    supportLevel: '',
    sector: '',
    industry: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  // Get auth token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  // Fetch screeners for dropdown
  useEffect(() => {
    if (isAdmin) {
      fetchScreeners();
    }
  }, [isAdmin]);

  // Fetch stocks when screener or page changes
  useEffect(() => {
    if (selectedScreener) {
      fetchStocks();
    }
  }, [selectedScreener, currentPage]);

  const fetchScreeners = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/screeners`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.data) {
        setScreeners(result.data);
        if (result.data.length > 0) {
          setSelectedScreener(result.data[0].code);
          setFormData(prev => ({ ...prev, screenerCode: result.data[0].code }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch screeners:', error);
      toast.error('Failed to load screeners');
    }
  };

  const fetchStocks = async () => {
    if (!selectedScreener) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/screener-stocks/${selectedScreener}?page=${currentPage}&pageSize=${pageSize}`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result.data) {
        setStocks(result.data.content || []);
        setTotalPages(result.data.totalPages || 1);
        setTotalElements(result.data.totalElements || 0);
      }
    } catch (error) {
      console.error('Failed to fetch stocks:', error);
      toast.error('Failed to load stocks');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSymbolBlur = async () => {
    const symbol = formData.symbol.trim().toUpperCase();
    if (!symbol || isEditMode || isCloneMode) return;

    setLookingUp(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/instrument-info?symbol=${encodeURIComponent(symbol)}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        toast.warn(`Symbol "${symbol}" not found in instrument master`);
        return;
      }
      const result = await response.json();
      const info = result.data;
      if (!info) return;

      setFormData(prev => ({
        ...prev,
        symbol,
        stockName: info.name || prev.stockName,
        capturedPrice: info.ohlcAvailable ? String(info.close) : prev.capturedPrice,
        resistanceLevel: info.ohlcAvailable ? String(info.high) : prev.resistanceLevel,
        supportLevel: info.ohlcAvailable ? String(info.low) : prev.supportLevel,
      }));

      if (info.ohlcAvailable) {
        toast.success(`Auto-filled ${symbol} — Close: ₹${info.close}, High: ₹${info.high}, Low: ₹${info.low}`);
      } else {
        toast.info(`Found ${symbol} (${info.name}) — OHLC not available yet`);
      }
    } catch (err) {
      console.error('Instrument lookup failed:', err);
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.screenerCode || !formData.symbol || !formData.stockName || !formData.capturedPrice) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditMode
        ? `${API_BASE_URL}/admin/screener-stocks/${editingStockId}`
        : `${API_BASE_URL}/admin/screener-stocks`;

      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          screenerCode: formData.screenerCode,
          symbol: formData.symbol.toUpperCase(),
          stockName: formData.stockName,
          exchange: formData.exchange,
          capturedPrice: parseFloat(formData.capturedPrice),
          capturedOn: formData.capturedOn,
          resistanceLevel: formData.resistanceLevel ? parseFloat(formData.resistanceLevel) : null,
          supportLevel: formData.supportLevel ? parseFloat(formData.supportLevel) : null,
          sector: formData.sector || null,
          industry: formData.industry || null,
          notes: formData.notes || null,
          ...(isCloneMode && {
            cloneDrawings,
            sourceStockId: cloningSourceStockId,
          }),
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to ${isEditMode ? 'update' : 'add'} stock`);
      }

      toast.success(`Stock ${isEditMode ? 'updated' : 'added'} successfully!`);
      closeDialog();
      fetchStocks();
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'add'} stock:`, error);
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'add'} stock`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingStock) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/screener-stocks/${deletingStock.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete stock');
      }

      toast.success(`Stock ${deletingStock.symbol} deleted successfully!`);
      setIsDeleteDialogOpen(false);
      setDeletingStock(null);
      fetchStocks();
    } catch (error: any) {
      console.error('Failed to delete stock:', error);
      toast.error(error.message || 'Failed to delete stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminate = async () => {
    if (!eliminatingStock) return;

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/screener-stocks/${eliminatingStock.id}/eliminate`,
        { method: 'PATCH', headers: getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to eliminate stock');
      }

      toast.success(`Stock ${eliminatingStock.symbol} eliminated successfully!`);
      setIsEliminateDialogOpen(false);
      setEliminatingStock(null);
      fetchStocks();
    } catch (error: any) {
      toast.error(error.message || 'Failed to eliminate stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVisibility = async (stock: ScreenerStock) => {
    try {
      const result = await screenerApi.toggleVisibility(stock.id);
      toast.success(result.message);
      fetchStocks();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle visibility');
    }
  };

  const handleToggleChartView = async () => {
    if (!selectedScreener) return;
    try {
      const result = await screenerApi.toggleChartView(selectedScreener);
      const newView = result.data.chartView;
      setScreeners(prev => prev.map(s => s.code === selectedScreener ? { ...s, chartView: newView } : s));
      toast.success(`Chart view set to ${newView} for ${selectedScreener}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle chart view');
    }
  };

  const currentChartView = screeners.find(s => s.code === selectedScreener)?.chartView ?? 'CLASSIC';

  const openEliminateDialog = (stock: ScreenerStock) => {
    setEliminatingStock(stock);
    setIsEliminateDialogOpen(true);
  };

  const openMovementDialog = (stock: ScreenerStock) => {
    setMovementStock(stock);
    setMovementPercent('');
    setMovementRecipientType('ALL');
    setIsMovementDialogOpen(true);
  };

  const handleSendMovementEmail = async () => {
    if (!movementStock || !movementPercent) return;

    setSendingMovement(true);
    try {
      const msg = await emailApi.sendSingleDayMovementEmail(
        movementStock.id,
        parseFloat(movementPercent),
        movementRecipientType
      );
      toast.success(msg);
      setIsMovementDialogOpen(false);
      setMovementStock(null);
      setMovementPercent('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingMovement(false);
    }
  };

  const openUploadDialog = () => {
    setUploadFile(null);
    setUploadResult(null);
    setUploadExchange('NSE');
    setIsUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    if (uploading) return;
    setIsUploadDialogOpen(false);
    setUploadFile(null);
    setUploadResult(null);
  };

  const handleUploadStocks = async () => {
    if (!uploadFile) { toast.error('Please select a CSV file'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem('accessToken');
      const form  = new FormData();
      form.append('file', uploadFile);
      const res  = await fetch(
        `${API_BASE_URL}/admin/stocks/upload?exchange=${uploadExchange}`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Upload failed');
      setUploadResult(json.data);
      toast.success(json.message);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      screenerCode: selectedScreener || '',
      symbol: '',
      stockName: '',
      exchange: 'NSE',
      capturedPrice: '',
      capturedOn: new Date().toISOString().split('T')[0],
      resistanceLevel: '',
      supportLevel: '',
      sector: '',
      industry: '',
      notes: ''
    });
    setIsEditMode(false);
    setIsCloneMode(false);
    setEditingStockId(null);
    setCloneDrawings(false);
    setCloningSourceStockId(null);
  };

  const openCloneDialog = (stock: ScreenerStock) => {
    setIsCloneMode(true);
    setIsEditMode(false);
    setCloningSourceStockId(stock.id);
    setCloneDrawings(false);
    setFormData({
      screenerCode: selectedScreener,
      symbol: stock.symbol,
      stockName: stock.stockName,
      exchange: stock.exchange || 'NSE',
      capturedPrice: stock.capturedPrice?.toString() || '',
      capturedOn: stock.capturedOn || new Date().toISOString().split('T')[0],
      resistanceLevel: stock.resistanceLevel?.toString() || '',
      supportLevel: stock.supportLevel?.toString() || '',
      sector: stock.sector || '',
      industry: stock.industry || '',
      notes: stock.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    resetForm();
    setFormData(prev => ({ ...prev, screenerCode: selectedScreener }));
    setIsDialogOpen(true);
  };

  const openEditDialog = (stock: ScreenerStock) => {
    setIsEditMode(true);
    setEditingStockId(stock.id);
    setFormData({
      screenerCode: selectedScreener,
      symbol: stock.symbol,
      stockName: stock.stockName,
      exchange: stock.exchange || 'NSE',
      capturedPrice: stock.capturedPrice?.toString() || '',
      capturedOn: stock.capturedOn || new Date().toISOString().split('T')[0],
      resistanceLevel: stock.resistanceLevel?.toString() || '',
      supportLevel: stock.supportLevel?.toString() || '',
      sector: stock.sector || '',
      industry: stock.industry || '',
      notes: stock.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (stock: ScreenerStock) => {
    setDeletingStock(stock);
    setIsDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="main-content">
        <div className="loading">Checking permissions...</div>
      </div>
    );
  }

  // Don't render if not admin (will redirect)
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="main-content">
      {/* Header */}
      <div className="admin-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h2 className="admin-title">Screener Stock Management</h2>
      </div>

      {/* Controls */}
      <div className="admin-controls">
        <div className="screener-selector">
          <label>Select Screener:</label>
          <select
            value={selectedScreener}
            onChange={(e) => {
              setSelectedScreener(e.target.value);
              setCurrentPage(1);
            }}
          >
            {screeners.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>
        <button className="add-stock-btn" onClick={openAddDialog}>
          <FaPlus /> Add Stock
        </button>
        <button className="upload-stocks-btn" onClick={openUploadDialog}>
          <FaUpload /> Upload Stocks
        </button>
        <button
          className={`chart-view-btn ${currentChartView === 'DRAWING' ? 'drawing-mode' : ''}`}
          onClick={handleToggleChartView}
          title={`Chart view: ${currentChartView}. Click to toggle.`}
        >
          {currentChartView === 'DRAWING' ? '🎨 Drawing Mode' : '📈 Classic Mode'}
        </button>
      </div>

      {/* Stock Count */}
      <div className="stock-count">
        Total Stocks: {totalElements}
      </div>

      {/* Stocks Table */}
      <div className="admin-table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : stocks.length === 0 ? (
          <div className="no-data">No stocks found for this screener</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Stock Name</th>
                <th>Exchange</th>
                <th>Captured Price</th>
                <th>Current Price</th>
                <th>Captured On</th>
                <th>Max Upside %</th>
                <th>Resistance Level</th>
                <th>Support Level</th>
                <th>Status</th>
                <th>Visibility</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stocks.map(stock => (
                <tr key={stock.id} className={stock.eliminated ? 'eliminated' : ''}>
                  <td>{stock.symbol}</td>
                  <td>{stock.stockName}</td>
                  <td>{stock.exchange}</td>
                  <td>{stock.capturedPrice?.toFixed(2)}</td>
                  <td>{stock.currentPrice?.toFixed(2) || '-'}</td>
                  <td>{stock.capturedOn}</td>
                  <td className={stock.maxUpside && stock.maxUpside > 0 ? 'positive' : 'negative'}>
                    {stock.maxUpside?.toFixed(2) || '-'}%
                  </td>
                  <td>{stock.resistanceLevel?.toFixed(2) || '-'}</td>
                  <td>{stock.supportLevel?.toFixed(2) || '-'}</td>
                  <td>
                    <span className={`status-badge ${stock.eliminated ? 'closed' : 'active'}`}>
                      {stock.eliminated ? 'Closed' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${stock.hiddenForFutureUsers ? 'hidden-future' : 'visible-all'}`}>
                      {stock.hiddenForFutureUsers ? 'Hidden (Future)' : 'All Users'}
                    </span>
                  </td>
                  <td className="notes-cell">{stock.notes || '-'}</td>
                  <td className="actions-cell">
                    <button
                      className="action-btn edit-btn"
                      onClick={() => openEditDialog(stock)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => openDeleteDialog(stock)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                    <button
                      className="action-btn clone-btn"
                      onClick={() => openCloneDialog(stock)}
                      title="Clone to another screener"
                    >
                      <FaCopy />
                    </button>
                    {!stock.eliminated && (
                      <button
                        className="action-btn eliminate-btn"
                        onClick={() => openEliminateDialog(stock)}
                        title="Eliminate (close) stock"
                      >
                        <FaBan />
                      </button>
                    )}
                    <button
                      className={`action-btn visibility-btn ${stock.hiddenForFutureUsers ? 'hidden-active' : ''}`}
                      onClick={() => handleToggleVisibility(stock)}
                      title={stock.hiddenForFutureUsers ? 'Visible to all (click to hide for future users)' : 'Click to hide for future users'}
                    >
                      {stock.hiddenForFutureUsers ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    <button
                      className="action-btn movement-btn"
                      onClick={() => openMovementDialog(stock)}
                      title="Send single-day movement % email"
                    >
                      <FaPaperPlane />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Stock Dialog */}
      {isDialogOpen && (
        <div className="dialog-overlay" onClick={closeDialog}>
          <div className="dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{isEditMode ? 'Edit Stock' : isCloneMode ? 'Clone Stock to Screener' : 'Add Stock to Screener'}</h3>
              <button className="close-btn" onClick={closeDialog}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Screener *</label>
                  <select
                    name="screenerCode"
                    value={formData.screenerCode}
                    onChange={handleInputChange}
                    required
                    disabled={isEditMode}
                  >
                    {screeners.map(s => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Exchange</label>
                  <select
                    name="exchange"
                    value={formData.exchange}
                    onChange={handleInputChange}
                  >
                    <option value="NSE">NSE</option>
                    <option value="BSE">BSE</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Symbol *</label>
                  <input
                    type="text"
                    name="symbol"
                    value={formData.symbol}
                    onChange={handleInputChange}
                    onBlur={handleSymbolBlur}
                    placeholder="e.g., RELIANCE"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                  {lookingUp && <span style={{ fontSize: 11, color: 'var(--accent)', marginTop: 3, display: 'block' }}>Looking up…</span>}
                </div>
                <div className="form-group">
                  <label>Stock Name *</label>
                  <input
                    type="text"
                    name="stockName"
                    value={formData.stockName}
                    onChange={handleInputChange}
                    placeholder="Auto-filled on symbol lookup"
                    required
                    readOnly={lookingUp}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Captured Price * <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>(auto: today's Close)</span></label>
                  <input
                    type="number"
                    name="capturedPrice"
                    value={formData.capturedPrice}
                    onChange={handleInputChange}
                    placeholder="Auto-filled from today's Close"
                    step="0.01"
                    min="0"
                    required
                    readOnly={lookingUp}
                  />
                </div>
                <div className="form-group">
                  <label>Captured On *</label>
                  <input
                    type="date"
                    name="capturedOn"
                    value={formData.capturedOn}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Resistance Level <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>(auto: today's High)</span></label>
                  <input
                    type="number"
                    name="resistanceLevel"
                    value={formData.resistanceLevel}
                    onChange={handleInputChange}
                    placeholder="Auto-filled from today's High"
                    step="0.01"
                    min="0"
                    readOnly={lookingUp}
                  />
                </div>
                <div className="form-group">
                  <label>Support Level <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>(auto: today's Low)</span></label>
                  <input
                    type="number"
                    name="supportLevel"
                    value={formData.supportLevel}
                    onChange={handleInputChange}
                    placeholder="Auto-filled from today's Low"
                    step="0.01"
                    min="0"
                    readOnly={lookingUp}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sector</label>
                  <input
                    type="text"
                    name="sector"
                    value={formData.sector}
                    onChange={handleInputChange}
                    placeholder="e.g., Energy"
                  />
                </div>
                <div className="form-group">
                  <label>Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="e.g., Oil & Gas"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>

              {isCloneMode && (
                <div className="form-group full-width clone-drawings-row">
                  <label className="clone-drawings-label">
                    <input
                      type="checkbox"
                      checked={cloneDrawings}
                      onChange={e => setCloneDrawings(e.target.checked)}
                    />
                    Also clone chart drawings to new screener
                  </label>
                </div>
              )}

              <div className="dialog-actions">
                <button type="button" className="cancel-btn" onClick={closeDialog}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting
                  ? (isEditMode ? 'Updating...' : 'Adding...')
                  : (isEditMode ? 'Update Stock' : isCloneMode ? 'Clone Stock' : 'Add Stock')
                }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Eliminate Confirmation Dialog */}
      {isEliminateDialogOpen && eliminatingStock && (
        <div className="dialog-overlay" onClick={() => setIsEliminateDialogOpen(false)}>
          <div className="dialog delete-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Eliminate Stock</h3>
              <button className="close-btn" onClick={() => setIsEliminateDialogOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="delete-content">
              <p>Are you sure you want to eliminate this stock?</p>
              <div className="delete-stock-info">
                <strong>{eliminatingStock.symbol}</strong> - {eliminatingStock.stockName}
              </div>
              <p className="delete-warning">Once eliminated, this stock cannot be reactivated.</p>
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setIsEliminateDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eliminate-confirm-btn"
                onClick={handleEliminate}
                disabled={submitting}
              >
                {submitting ? 'Eliminating...' : 'Eliminate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Movement Email Dialog */}
      {isMovementDialogOpen && movementStock && (
        <div className="dialog-overlay" onClick={() => !sendingMovement && setIsMovementDialogOpen(false)}>
          <div className="dialog movement-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Send Movement Email</h3>
              <button className="close-btn" onClick={() => setIsMovementDialogOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="movement-content">
              <div className="movement-stock-info">
                <strong>{movementStock.symbol}</strong> - {movementStock.stockName}
                <span className="movement-screener-tag">
                  {screeners.find(s => s.code === selectedScreener)?.name || selectedScreener}
                </span>
                <span className="movement-captured-on">Captured on {movementStock.capturedOn}</span>
              </div>

              <div className="form-group">
                <label>Single-Day Movement (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 8 or 10"
                  value={movementPercent}
                  onChange={e => setMovementPercent(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Send To</label>
                <div className="movement-radio-group">
                  {([
                    { value: 'ALL', label: 'All Users' },
                    { value: 'PREMIUM', label: 'Premium Users' },
                    { value: 'FREE', label: 'Free Users' },
                  ] as { value: 'ALL' | 'PREMIUM' | 'FREE'; label: string }[]).map(opt => (
                    <label
                      key={opt.value}
                      className={`movement-radio-option ${movementRecipientType === opt.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="movementRecipient"
                        value={opt.value}
                        checked={movementRecipientType === opt.value}
                        onChange={() => setMovementRecipientType(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setIsMovementDialogOpen(false)}
                disabled={sendingMovement}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-btn movement-send-btn"
                onClick={handleSendMovementEmail}
                disabled={sendingMovement || !movementPercent}
              >
                <FaPaperPlane />
                {sendingMovement ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Stocks Dialog */}
      {isUploadDialogOpen && (
        <div className="dialog-overlay" onClick={closeUploadDialog}>
          <div className="dialog upload-dialog" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="dialog-header">
              <div className="upload-header-text">
                <h3>Upload Stocks CSV</h3>
                <span className="upload-header-sub">Populate the stock master for symbol autocomplete</span>
              </div>
              <button className="close-btn" onClick={closeUploadDialog} disabled={uploading}>
                <FaTimes />
              </button>
            </div>

            {!uploadResult ? (
              <div className="upload-dialog-body">

                {/* Info strip */}
                <div className="upload-info-strip">
                  <div className="upload-info-col">
                    <span className="upload-info-label">Required columns</span>
                    <div className="upload-info-chips">
                      <span className="upload-chip required">symbol</span>
                      <span className="upload-chip">name</span>
                      <span className="upload-chip">company name</span>
                      <span className="upload-chip">stock name</span>
                    </div>
                  </div>
                  <div className="upload-info-note">Any column order · Any case · Duplicates auto-skipped</div>
                </div>

                {/* Exchange + Drop zone row */}
                <div className="upload-body-row">
                  <div className="upload-exchange-box">
                    <label className="upload-exchange-label">Exchange</label>
                    <div className="upload-exchange-toggle">
                      {['NSE', 'BSE'].map(ex => (
                        <button
                          key={ex}
                          type="button"
                          className={`upload-ex-btn ${uploadExchange === ex ? 'active' : ''}`}
                          onClick={() => setUploadExchange(ex)}
                        >
                          {ex}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Drop zone */}
                <div
                  className={`upload-drop-zone ${uploadFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
                  />
                  {uploadFile ? (
                    <>
                      <FaCheckCircle className="upload-drop-icon success" />
                      <span className="upload-file-name">{uploadFile.name}</span>
                      <span className="upload-file-hint">Click to change file</span>
                    </>
                  ) : (
                    <>
                      <FaUpload className="upload-drop-icon" />
                      <span className="upload-drop-title">Click to select CSV file</span>
                      <span className="upload-file-hint">Supports .csv files only</span>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="upload-actions">
                  <button className="cancel-btn" onClick={closeUploadDialog} disabled={uploading}>Cancel</button>
                  <button
                    className="submit-btn upload-submit-btn"
                    onClick={handleUploadStocks}
                    disabled={uploading || !uploadFile}
                  >
                    {uploading
                      ? <><span className="upload-spinner" /> Uploading…</>
                      : <><FaUpload style={{ marginRight: 6 }} /> Upload Stocks</>
                    }
                  </button>
                </div>
              </div>

            ) : (
              <div className="upload-result-body">

                {/* Success banner */}
                <div className="upload-success-banner">
                  <FaCheckCircle className="upload-banner-icon" />
                  <div>
                    <div className="upload-banner-title">{uploadResult.inserted} stocks added successfully</div>
                    <div className="upload-banner-sub">{uploadExchange} · {uploadFile?.name}</div>
                  </div>
                </div>

                {/* Stats row */}
                <div className="upload-stats-row">
                  <div className="upload-stat total">
                    <span className="upload-stat-num">{uploadResult.total}</span>
                    <span className="upload-stat-lbl">Total in CSV</span>
                  </div>
                  <div className="upload-stat-divider" />
                  <div className="upload-stat inserted">
                    <span className="upload-stat-num">{uploadResult.inserted}</span>
                    <span className="upload-stat-lbl">Added</span>
                  </div>
                  <div className="upload-stat-divider" />
                  <div className="upload-stat skipped">
                    <span className="upload-stat-num">{uploadResult.skipped}</span>
                    <span className="upload-stat-lbl">Skipped</span>
                  </div>
                  {uploadResult.failed > 0 && (
                    <>
                      <div className="upload-stat-divider" />
                      <div className="upload-stat failed">
                        <span className="upload-stat-num">{uploadResult.failed}</span>
                        <span className="upload-stat-lbl">Failed</span>
                      </div>
                    </>
                  )}
                </div>

                {/* New stocks list */}
                {uploadResult.newStocks.length > 0 && (
                  <div className="upload-new-stocks">
                    <div className="upload-new-stocks-header">
                      <span>Newly Added Stocks</span>
                      <span className="upload-count-badge">{uploadResult.inserted}</span>
                    </div>
                    <div className="upload-stocks-list">
                      {uploadResult.newStocks.map((s, i) => (
                        <div key={s.symbol} className="upload-stock-row">
                          <span className="upload-row-num">{i + 1}</span>
                          <span className="upload-stock-symbol">{s.symbol}</span>
                          <span className="upload-stock-name">{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="upload-actions">
                  <button className="submit-btn" onClick={closeUploadDialog}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && deletingStock && (
        <div className="dialog-overlay" onClick={() => setIsDeleteDialogOpen(false)}>
          <div className="dialog delete-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm Delete</h3>
              <button className="close-btn" onClick={() => setIsDeleteDialogOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="delete-content">
              <p>Are you sure you want to delete this stock?</p>
              <div className="delete-stock-info">
                <strong>{deletingStock.symbol}</strong> - {deletingStock.stockName}
              </div>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-confirm-btn"
                onClick={handleDelete}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminScreenerStocks;
